/**
 * Image Service - Provides utilities for image loading optimization and Firebase Storage handling
 */

export interface ImageLoadMetrics {
	url: string;
	loadTime: number;
	status: "success" | "error" | "retry";
	attempts: number;
	timestamp: number;
}

class ImageService {
	private metrics: ImageLoadMetrics[] = [];
	private readonly maxMetrics = 100;

	/**
	 * Add image load metrics for monitoring
	 */
	addMetric(metric: ImageLoadMetrics): void {
		this.metrics.push(metric);
		if (this.metrics.length > this.maxMetrics) {
			this.metrics.shift();
		}
	}

	/**
	 * Get image load performance metrics
	 */
	getMetrics(): ImageLoadMetrics[] {
		return this.metrics;
	}

	/**
	 * Get success rate for image loads
	 */
	getSuccessRate(): number {
		if (this.metrics.length === 0) return 1;
		const successful = this.metrics.filter(
			(m) => m.status === "success"
		).length;
		return successful / this.metrics.length;
	}

	/**
	 * Get average load time
	 */
	getAverageLoadTime(): number {
		if (this.metrics.length === 0) return 0;
		const successful = this.metrics.filter((m) => m.status === "success");
		if (successful.length === 0) return 0;
		return (
			successful.reduce((sum, m) => sum + m.loadTime, 0) / successful.length
		);
	}

	/**
	 * Optimize Firebase Storage URL for better caching
	 */
	optimizeFirebaseUrl(url: string): string {
		// Add cache-busting parameters if not present
		if (url.includes("firebasestorage.googleapis.com")) {
			const separator = url.includes("?") ? "&" : "?";
			return `${url}${separator}t=${Date.now()}`;
		}
		return url;
	}

	/**
	 * Ensure Firebase Storage URL has proper token parameter
	 */
	normalizeFirebaseUrl(url: string): string {
		if (url.includes("firebasestorage.googleapis.com")) {
			// Firebase Storage URLs sometimes need proper token handling
			let normalizedUrl = url;

			// Add alt=media parameter if not present
			if (!normalizedUrl.includes("alt=media")) {
				const separator = normalizedUrl.includes("?") ? "&" : "?";
				normalizedUrl = `${normalizedUrl}${separator}alt=media`;
			}

			// Add token parameter if not present (Firebase Storage sometimes requires this)
			if (!normalizedUrl.includes("token=")) {
				const separator = normalizedUrl.includes("?") ? "&" : "?";
				normalizedUrl = `${normalizedUrl}${separator}token=${Date.now()}`;
			}

			return normalizedUrl;
		}
		return url;
	}

	/**
	 * Generate optimized thumbnail URL from Firebase Storage image
	 */
	generateThumbnailUrl(url: string, size: number = 200): string {
		if (url.includes("firebasestorage.googleapis.com")) {
			// For Firebase Storage, we can add size parameters
			// Note: This assumes Firebase Storage images can be resized
			return `${url}&size=${size}`;
		}
		return url;
	}

	/**
	 * Check if URL is from Firebase Storage
	 */
	isFirebaseStorage(url: string): boolean {
		return url.includes("firebasestorage.googleapis.com");
	}

	/**
	 * Preload image with monitoring
	 */
	preloadImage(
		url: string,
		onLoad?: () => void,
		onError?: () => void
	): Promise<void> {
		return new Promise((resolve, reject) => {
			const startTime = Date.now();
			const img = new Image();

			img.onload = () => {
				const loadTime = Date.now() - startTime;
				this.addMetric({
					url,
					loadTime,
					status: "success",
					attempts: 1,
					timestamp: Date.now(),
				});
				onLoad?.();
				resolve();
			};

			img.onerror = () => {
				const loadTime = Date.now() - startTime;
				this.addMetric({
					url,
					loadTime,
					status: "error",
					attempts: 1,
					timestamp: Date.now(),
				});
				onError?.();
				reject(new Error(`Failed to preload image: ${url}`));
			};

			img.src = this.optimizeFirebaseUrl(url);
		});
	}

	/**
	 * Batch preload multiple images
	 */
	batchPreload(
		images: string[],
		onProgress?: (loaded: number, total: number) => void
	): Promise<void> {
		const promises = images.map((url, index) => {
			return this.preloadImage(url, () => {
				onProgress?.(index + 1, images.length);
			});
		});

		return Promise.allSettled(promises).then(() => {
			// All images have been processed (some may have failed)
		});
	}

	/**
	 * Clear metrics cache
	 */
	clearMetrics(): void {
		this.metrics = [];
	}

	/**
	 * Export metrics for debugging
	 */
	exportMetrics(): string {
		return JSON.stringify(this.metrics, null, 2);
	}
}

// Export singleton instance
export const imageService = new ImageService();

// Export class for testing or custom instances
export default ImageService;
